import { useMemo, useContext, useEffect, useState } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { Box, Grid, Container, Paper, Typography } from '@mui/material';
import { jwtDecode } from 'jwt-decode';
import { useTranslation } from 'react-i18next';
import { getPath } from '../../Pages';
import useGameService from '../../../services/GameService';
import { useGetGameLink, useLoader } from '../../../lib/hooks';
import Details from '../../../components/public/game/Game/Details';
import Actions from '../../../components/public/game/GameConfirmation/Actions';
import type { Occupant } from '../../../types/models/Occupant';
import useUsersService from '../../../services/UsersService';
import { AppContext } from '../../../sporttia/AppContext';

export default function GameConfirmation() {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const cxt = useContext(AppContext)!;
    const { t } = useTranslation();
    const { search: searchParams } = useLocation();
    const { useGetGame, usePostGamePlayer } = useGameService();
    const { useConfirmUser, useGetUser } = useUsersService();
    const { mutate: confirmUser, status: confirmStatus } = useConfirmUser();

    const [loginReady, setLoginReady] = useState(false);

    const { loginToken, signature, userId } = useMemo(() => {
        const query = new URLSearchParams(searchParams);
        const data = query.get('data');
        if (!data) {
            return {
                loginToken: '',
                signature: '',
                userId: 0,
            };
        }

        const decoded = jwtDecode<{
            loginToken: string;
            signature: string;
            userId: string;
            email: string;
        }>(data);

        return {
            loginToken: decoded.loginToken,
            signature: decoded.signature,
            userId: parseInt(decoded.userId),
        };
    }, [searchParams]);

    useEffect(() => {
        cxt.setLoggedUser(false);
        setLoginReady(false);
    }, []);

    const {
        status: getUserStatus,
        data: getUserData,
        error: getUserError,
    } = useGetUser(userId);

    // Handle user data
    useEffect(() => {
        if (getUserData?.user?.status === 'ACTIVE') {
            cxt.setLoggedUser({ ...getUserData.user, token: loginToken });
            setLoginReady(true);
            return;
        }

        if (getUserData?.user && getUserData.user.status !== 'ACTIVE') {
            confirmUser(
                {
                    userId,
                    params: { signature },
                },
                {
                    onSuccess: () => {
                        cxt.setLoggedUser({
                            ...getUserData.user,
                            token: loginToken,
                        });
                        setLoginReady(true);
                    },
                    onError: () => {
                        setLoginReady(false);
                    },
                },
            );
        }
    }, [getUserData, cxt, loginToken, userId, signature, confirmUser]);

    // Handle user error
    useEffect(() => {
        if (getUserError) {
            setLoginReady(false);
        }
    }, [getUserError]);

    const { data: gameResponse, status: gameStatus } = useGetGame(
        parseInt(id || '0'),
        { enabled: loginReady } as any,
    );
    const { mutate: postPlayer, isPending: loadingPlayer } = usePostGamePlayer(
        parseInt(id || '0'),
    );

    const { game } = useMemo(
        () => ({
            game: gameResponse?.game,
        }),
        [gameResponse],
    );

    const { link: gameLink } = useGetGameLink().getGameLink(game);

    const handlePostPlayer = (player: Occupant, paymentForm: string) => {
        postPlayer(
            {
                params: {
                    userId: player.id,
                    paymentForm,
                    rates: player.selectedRate
                        ? player.selectedRate.prices.map((price: any) => ({
                              id: price.id,
                              duration: price.duration,
                          }))
                        : undefined,
                    idBoleto: player.selectedBoleto?.id,
                },
            },
            {
                onSuccess: (response: any) => {
                    if (response.tpv) {
                        navigate(
                            getPath('tpvSwitcher', {
                                state: {
                                    id: response.tpv.id,
                                    gameId: id,
                                },
                            }),
                        );
                        return;
                    }
                    window.location.href = gameLink;
                },
                onError: (err: unknown) => {
                    const apiErr = err as any;
                    const errorMsg =
                        apiErr?.error?.msg || t('Error en la inscripción');
                    cxt.showMessage('E', errorMsg);
                },
            },
        );
    };

    const [isLoading, loader] = useLoader([
        gameStatus,
        confirmStatus,
        getUserStatus,
        loginReady ? 'success' : 'loading',
    ]);

    if (!loginReady || isLoading) {
        return <Box style={{ minHeight: '100vh', padding: 24 }}>{loader}</Box>;
    }

    if (!game) return null;

    return (
        <Box style={{ minHeight: '100vh', padding: 24 }}>
            {!isLoading && (
                <Container maxWidth="md">
                    <Paper style={{ marginBottom: 24, padding: 20 }}>
                        <Typography
                            variant="h5"
                            style={{ fontWeight: 700, marginBottom: 10 }}
                        >
                            {t('gameDetails')}
                        </Typography>

                        <Details game={game} />
                    </Paper>

                    <Grid container spacing={3} justifyContent="center">
                        <Actions
                            scId={game.facility.sc.id}
                            game={game}
                            handleBook={handlePostPlayer}
                            loading={loadingPlayer}
                        />
                    </Grid>
                </Container>
            )}
        </Box>
    );
}
